import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { PlansSection } from "@/components/landing/PlansSection";
import { AlreadyBought } from "@/components/landing/AlreadyBought";
import { StampsSection } from "@/components/landing/StampsSection";
import { AuthorizedDistributors } from "@/components/landing/AuthorizedDistributors";
import { ProductShowcase } from "@/components/landing/ProductShowcase";
import { useCatalog } from "@/lib/plans";
import { mxn } from "@/lib/api";
import {
  Check, ShoppingCart, Handshake, Store, UtensilsCrossed, ArrowRight,
  Zap, ShieldCheck, BarChart3,
} from "lucide-react";

const IMG = {
  hero: "/img/hero.webp?v=2",
  store: "/img/pos/tienda.webp",
  restaurant: "/img/restaurant.webp?v=2",
  partnership: "/img/partnership.webp?v=2",
};

const PRODUCTS = [
  { id: "profesionalpos", name: "ProfesionalPOS", tag: "Comercios y retail", icon: Store, img: IMG.store },
  { id: "profesionalpos_restaurante", name: "ProfesionalPOS Restaurante", tag: "Restaurantes y cafeterías", icon: UtensilsCrossed, img: IMG.restaurant },
];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

function Hero({ fromPrice, onBuy }) {
  const navigate = useNavigate();
  return (
    <section className="relative overflow-hidden bg-foreground">
      <img src={IMG.hero} alt="Sistema punto de venta" fetchPriority="high" className="absolute inset-0 h-full w-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/30" />
      <div className="relative mx-auto max-w-7xl px-5 py-28 lg:px-8 lg:py-36">
        <div className="max-w-2xl animate-fade-up">
          <span className="overline inline-block rounded-full border border-white/20 px-4 py-1.5 text-white/80">Punto de venta profesional</span>
          <h1 className="mt-6 font-display text-5xl font-black leading-[1.05] tracking-tighter text-white md:text-6xl">
            Vende más rápido con <span className="text-primary">ProfesionalPOS</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/70">
            El software de punto de venta para comercios y restaurantes. Elige tu plan mensual o anual, o conviértete en distribuidor y genera ingresos revendiendo licencias.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button size="lg" className="rounded-full" onClick={onBuy} data-testid="hero-buy-btn">
              <ShoppingCart className="mr-1 h-4 w-4" /> Ver planes {fromPrice ? `· desde ${mxn(fromPrice)}/mes` : ""}
            </Button>
            <Button size="lg" variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white hover:text-foreground" onClick={() => navigate("/registro-distribuidor")} data-testid="hero-distributor-btn">
              <Handshake className="mr-1 h-4 w-4" /> Ser distribuidor
            </Button>
          </div>
          <div className="mt-12 flex flex-wrap gap-8">
            {[
              { icon: Zap, t: "Activación", d: "ID de activación por correo" },
              { icon: ShieldCheck, t: "Mensual o anual", d: "Renovación simple" },
              { icon: BarChart3, t: "Reportes", d: "Ventas en tiempo real" },
            ].map((s) => (
              <div key={s.t} className="flex items-center gap-3">
                <s.icon className="h-5 w-5 text-primary" />
                <div><p className="text-sm font-semibold text-white">{s.t}</p><p className="text-xs text-white/50">{s.d}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ProductsSection({ catalog, onSeePlans }) {
  const sys = (id) => catalog?.system_features?.[id] || { features: [], giros: [] };
  return (
    <section id="productos" className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
      <div className="max-w-2xl">
        <p className="overline text-primary">Dos soluciones</p>
        <h2 className="mt-3 font-display text-4xl font-extrabold tracking-tight md:text-5xl">Elige el sistema para tu negocio</h2>
      </div>
      <div className="mt-14 grid gap-8 lg:grid-cols-2">
        {PRODUCTS.map((p, i) => (
          <div key={p.id} className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition-shadow duration-300 hover:shadow-xl animate-fade-up" style={{ animationDelay: `${i * 100}ms` }} data-testid={`product-card-${p.id}`}>
            <div className="relative h-56 overflow-hidden">
              <img src={p.img} alt={p.name} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-4 left-5 flex items-center gap-2 text-white"><p.icon className="h-5 w-5" /><span className="overline">{p.tag}</span></div>
            </div>
            <div className="flex flex-1 flex-col p-7">
              <h3 className="font-display text-2xl font-bold tracking-tight">{p.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">Funciones del sistema:</p>
              <ul className="mt-5 grid flex-1 content-start gap-x-6 gap-y-2.5 sm:grid-cols-2" data-testid={`product-features-${p.id}`}>
                {sys(p.id).features.map((f, k) => (
                  <li key={`${k}-${f}`} className="flex items-start gap-2.5 text-sm text-muted-foreground"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" strokeWidth={2.5} />{f}</li>
                ))}
              </ul>
              <div className="mt-6 border-t border-border pt-5" data-testid={`product-giros-${p.id}`}>
                <p className="overline text-foreground">Giros compatibles</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {sys(p.id).giros.map((g, k) => <span key={`${k}-${g}`} className="rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">{g}</span>)}
                </div>
              </div>
              <Button onClick={() => onSeePlans(p.id)} className="mt-7 w-full rounded-full" data-testid={`product-buy-${p.id}`}>
                Ver planes de {p.name.split(" ").pop()} <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function DistributorSection({ tiers }) {
  const navigate = useNavigate();
  return (
    <section id="distribuidores" className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <div className="animate-fade-up">
          <p className="overline text-primary">Programa de distribuidores</p>
          <h2 className="mt-3 font-display text-4xl font-extrabold tracking-tight md:text-5xl">Convierte ProfesionalPOS en tu negocio</h2>
          <p className="mt-5 leading-relaxed text-muted-foreground">
            Regístrate con tu Constancia de Situación Fiscal, obtén la aprobación de nuestro equipo y compra licencias con descuento según tu nivel. Paga con Mercado Pago o solicítalas como adeudo, revéndelas al precio que tú definas y retira tu saldo a tu cuenta bancaria cuando quieras.
          </p>
          <div className="mt-8 overflow-hidden rounded-xl border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-secondary/60"><tr className="border-b border-border">
                <th className="px-5 py-3 font-semibold">Nivel</th><th className="px-5 py-3 font-semibold">Licencias adquiridas</th><th className="px-5 py-3 font-semibold">Descuento</th>
              </tr></thead>
              <tbody>
                {(tiers || []).map((t, i) => (
                  <tr key={t.id} className={i < 2 ? "border-b border-border" : ""} data-testid={`landing-tier-${t.id}`}>
                    <td className="px-5 py-3">Fase {t.level} · {t.name}</td>
                    <td className="px-5 py-3">{t.max ? `${Math.max(1, t.min)} a ${t.max}` : `${t.min} a 30`}</td>
                    <td className="px-5 py-3 font-semibold text-primary">{t.discount}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button size="lg" className="mt-8 rounded-full" onClick={() => navigate("/registro-distribuidor")} data-testid="distributor-cta-btn">
            Empezar como distribuidor <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
        <div className="relative overflow-hidden rounded-2xl border border-border">
          <img src={IMG.partnership} alt="Alianza de distribuidores" loading="lazy" decoding="async" className="h-full max-h-[80vh] w-full object-cover" />
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const catalog = useCatalog();
  const [product, setProduct] = useState("profesionalpos");
  const [period, setPeriod] = useState("annual");
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) setTimeout(() => scrollTo(hash.slice(1)), 300);
  }, [hash]);

  const seePlans = (p) => { setProduct(p); scrollTo("precios"); };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <Hero fromPrice={catalog?.products.profesionalpos.plans.basico.monthly} onBuy={() => scrollTo("precios")} />
      <ProductsSection catalog={catalog} onSeePlans={seePlans} />
      <ProductShowcase />
      <PlansSection catalog={catalog} product={product} setProduct={setProduct} period={period} setPeriod={setPeriod} />
      <StampsSection />
      <AlreadyBought />
      <DistributorSection tiers={catalog?.tiers} />
      <AuthorizedDistributors />
      <Footer />
    </div>
  );
}
